import { useCallback, useRef } from 'react'

const INTERACTIVE_SELECTOR = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function isEditable(element) {
  return Boolean(element?.closest?.('input, textarea, select, [contenteditable="true"]'))
}

function isAvailable(element) {
  return !element.closest?.('[hidden], [aria-hidden="true"]') && element.getAttribute('aria-disabled') !== 'true'
}

export default function WorkspaceColumnKeyboardSurface({
  label,
  commandTarget,
  tabs = [],
  activeTab,
  onTabChange,
  className = '',
  children,
  onKeyDown,
  ...surfaceProps
}) {
  const rootRef = useRef(null)

  const handleKeyDown = useCallback((event) => {
    onKeyDown?.(event)
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return

    const root = rootRef.current
    if (!root || isEditable(event.target)) return

    if (event.key === '[' || event.key === ']') {
      if (tabs.length === 0 || typeof onTabChange !== 'function') return
      const currentIndex = Math.max(0, tabs.indexOf(activeTab))
      const direction = event.key === ']' ? 1 : -1
      const nextIndex = (currentIndex + direction + tabs.length) % tabs.length
      event.preventDefault()
      event.stopPropagation()
      onTabChange(tabs[nextIndex])
      root.focus({ preventScroll: true })
      return
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const activePanel = root.querySelector('[data-column-panel-active="true"]') || root
      const controls = Array.from(activePanel.querySelectorAll(INTERACTIVE_SELECTOR)).filter(isAvailable)
      if (controls.length === 0) return
      const currentIndex = controls.indexOf(document.activeElement)
      const nextIndex = event.key === 'ArrowDown'
        ? Math.min(controls.length - 1, currentIndex + 1)
        : currentIndex < 0 ? controls.length - 1 : Math.max(0, currentIndex - 1)
      event.preventDefault()
      event.stopPropagation()
      controls[nextIndex].focus({ preventScroll: true })
      controls[nextIndex].scrollIntoView?.({ block: 'nearest' })
      return
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      root.blur()
    }
  }, [activeTab, onKeyDown, onTabChange, tabs])

  return (
    <div
      ref={rootRef}
      role="region"
      aria-label={label}
      tabIndex={-1}
      data-command-target={commandTarget}
      onKeyDown={handleKeyDown}
      className={`${className} outline-none focus-visible:ring-2 focus-visible:ring-ink/40 focus-visible:ring-inset`}
      {...surfaceProps}
    >
      {children}
    </div>
  )
}
